import { computed, type ComputedRef } from 'vue'
import { useLocaleStore } from '@/stores/locale'
import {
  formatDate as formatDateInLocale,
  formatDayLabel as formatDayLabelInLocale,
  formatRelativeTime as formatRelativeTimeInLocale,
  weekdayLabel,
  type DateInput,
  type DateStyle,
} from '@/lib/format/date'
import { formatPrice as formatPriceInLocale } from '@/lib/billing/format'
import {
  formatCompactNumber as formatCompactNumberInLocale,
  formatDecimal as formatDecimalInLocale,
  formatFixed as formatFixedInLocale,
} from '@/lib/format/number'
import { formatFileSize as formatFileSizeInLocale } from '@/lib/format/bytes'

export interface Formatters {
  /** The active UI locale, e.g. `'en'`. */
  locale: ComputedRef<string>
  /** Renders `value` in one of the closed set of `DateStyle`s, in the active locale. */
  formatDate: (value: DateInput, style: DateStyle) => string
  /** Renders `value`'s time of day in the active locale (12h or 24h, whichever the locale uses). */
  formatTime: (value: DateInput) => string
  /** `formatDate(value, dateStyle)` and `formatTime(value)`, joined - the day-plus-time pattern used across content and documents. */
  formatDateTime: (value: DateInput, dateStyle?: DateStyle) => string
  /** The narrow weekday label for `day` (0 = Monday .. 6 = Sunday), in the active locale. */
  weekdayNarrow: (day: number) => string
  /** The short weekday label for `day` (0 = Monday .. 6 = Sunday), in the active locale. */
  weekdayShort: (day: number) => string
  /** The full weekday name for `day` (0 = Monday .. 6 = Sunday), in the active locale - for prose, not axis ticks. */
  weekdayLong: (day: number) => string
  /** Relative-time phrasing against `now` (defaults to render time), in the active locale. Works both ways: "5 minutes ago" and "in 5 minutes". */
  formatRelativeTime: (value: DateInput, now?: Date) => string
  /** Calendar-day group label against `now` (defaults to render time), in the active locale: "today", "yesterday", or an absolute date. */
  formatDayLabel: (value: DateInput, now?: Date) => string
  /** Formats a minor-unit amount as currency in the active locale. */
  formatPrice: (minorUnits: number, currency: string) => string
  /** Renders a count in compact notation (`12.4K`, `1.2M`) in the active locale; `''` when absent. */
  formatCompactNumber: (value: number | null | undefined) => string
  /** Renders a number in full digits with at most `maximumFractionDigits` decimals, in the active locale. */
  formatDecimal: (value: number, maximumFractionDigits?: number) => string
  /** Renders a number with exactly `fractionDigits` decimals (default 1), in the active locale. */
  formatFixed: (value: number, fractionDigits?: number) => string
  /** Renders a byte count at its largest readable unit, in the active locale. */
  formatFileSize: (bytes: number) => string
  /** Renders a 0-1 fraction as a whole-number percent in the active locale. */
  formatPercent: (fraction: number) => string
}

/** The one place UI code reads dates, times and prices; forwards the active locale to the framework-free primitives. */
export function useFormatters(): Formatters {
  const localeStore = useLocaleStore()
  const locale = computed(() => localeStore.locale)

  function formatDate(value: DateInput, style: DateStyle): string {
    return formatDateInLocale(value, style, locale.value)
  }

  function formatTime(value: DateInput): string {
    return formatDateInLocale(value, 'time', locale.value)
  }

  function formatDateTime(value: DateInput, dateStyle: DateStyle = 'weekdayMonthDay'): string {
    const day = formatDate(value, dateStyle)
    const time = formatTime(value)
    return day && time ? `${day} · ${time}` : day || time
  }

  function weekdayNarrow(day: number): string {
    return weekdayLabel(locale.value, day, 'narrow')
  }

  function weekdayShort(day: number): string {
    return weekdayLabel(locale.value, day, 'short')
  }

  function weekdayLong(day: number): string {
    return weekdayLabel(locale.value, day, 'long')
  }

  function formatRelativeTime(value: DateInput, now: Date = new Date()): string {
    return formatRelativeTimeInLocale(value, now, locale.value)
  }

  function formatDayLabel(value: DateInput, now: Date = new Date()): string {
    return formatDayLabelInLocale(value, now, locale.value)
  }

  function formatPrice(minorUnits: number, currency: string): string {
    return formatPriceInLocale(minorUnits, currency, locale.value)
  }

  function formatCompactNumber(value: number | null | undefined): string {
    return formatCompactNumberInLocale(value, locale.value)
  }

  function formatDecimal(value: number, maximumFractionDigits = 1): string {
    return formatDecimalInLocale(value, locale.value, maximumFractionDigits)
  }

  function formatFixed(value: number, fractionDigits = 1): string {
    return formatFixedInLocale(value, locale.value, fractionDigits)
  }

  function formatFileSize(bytes: number): string {
    return formatFileSizeInLocale(bytes, locale.value)
  }

  function formatPercent(fraction: number): string {
    return `${formatDecimalInLocale(fraction * 100, locale.value, 0)}%`
  }

  return {
    locale,
    formatDate,
    formatTime,
    formatDateTime,
    weekdayNarrow,
    weekdayShort,
    weekdayLong,
    formatRelativeTime,
    formatDayLabel,
    formatPrice,
    formatCompactNumber,
    formatDecimal,
    formatFixed,
    formatPercent,
    formatFileSize,
  }
}
